import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Loader2, Briefcase, ListOrdered, ArrowRight, Clock, DollarSign, Tag, Eye } from 'lucide-react';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const STATUS_PILL = {
  open:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
  awarded: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/20',
  closed:  'bg-zinc-500/15 text-zinc-300 border-zinc-500/20',
  expired: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/20',
};

/**
 * /jobs — Two tabs:
 *   Open Requests   — vendors browse RFQs (auto-filtered to their category)
 *   My Requests     — consumer's own posted RFQs
 *   My Offers       — vendor's submitted offers (only visible if vendor)
 */
export default function JobRequestsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isVendor = !!(user?.roles?.includes('vendor') || user?.role === 'vendor');
  const [tab, setTab] = useState(isVendor ? 'feed' : 'mine');
  const [feed, setFeed] = useState([]);
  const [mine, setMine] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        if (tab === 'feed') {
          const res = await fetch(`${API}/rfq/feed?limit=30`, { headers: user ? headers() : {} });
          if (res.ok) setFeed((await res.json()).requests || []);
        } else if (tab === 'mine') {
          if (!user) { setMine([]); return; }
          const res = await fetch(`${API}/rfq/me`, { headers: headers() });
          if (res.ok) setMine((await res.json()).requests || []);
        } else if (tab === 'offers') {
          if (!user) { setOffers([]); return; }
          const res = await fetch(`${API}/rfq/me/offers`, { headers: headers() });
          if (res.ok) setOffers((await res.json()).offers || []);
        }
      } finally { setLoading(false); }
    })();
  }, [tab, user, headers]);

  const TabBtn = ({ id, icon: Icon, children, count }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
        tab === id
          ? 'bg-yellow-400 text-black shadow-[0_4px_18px_-4px_rgba(250,204,21,0.5)]'
          : 'bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/10'
      }`}
      data-testid={`jobs-tab-${id}`}
    >
      <Icon size={14} /> {children}
      {count !== undefined && count > 0 && (
        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-black/20 text-[10px] font-bold">{count}</span>
      )}
    </button>
  );

  const list = tab === 'feed' ? feed : tab === 'mine' ? mine : offers;
  const empty = !loading && list.length === 0;

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 pt-24 pb-12" data-testid="jobs-page">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-syne">Job Requests</h1>
            <p className="text-zinc-400 text-sm mt-1">Buyers post, vendors quote — Indigenous business at the speed of demand.</p>
          </div>
          <button
            onClick={() => navigate('/jobs/new')}
            className="flex-shrink-0 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white text-sm font-bold flex items-center gap-2 shadow-[0_4px_24px_-4px_rgba(168,85,247,0.7)] border border-purple-300/40"
            data-testid="jobs-new-cta"
          >
            <Plus size={14} /> Request a Job
          </button>
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <TabBtn id="feed" icon={Briefcase}>Open Requests</TabBtn>
          {user && <TabBtn id="mine" icon={ListOrdered}>My Requests</TabBtn>}
          {user && isVendor && <TabBtn id="offers" icon={DollarSign}>My Offers</TabBtn>}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-fuchsia-400" /></div>
        ) : empty ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
            <p className="text-white font-semibold">
              {tab === 'feed' ? 'No open requests right now' : tab === 'mine' ? 'You haven\'t posted any requests yet' : 'You haven\'t made any offers yet'}
            </p>
            <p className="text-zinc-400 text-xs mt-1">
              {tab === 'feed'
                ? 'When buyers post jobs in your category, they\'ll show up here.'
                : tab === 'mine'
                  ? 'Post a request and let vendors come to you.'
                  : 'Browse open requests and submit offers to win work.'}
            </p>
            {tab === 'mine' && (
              <button onClick={() => navigate('/jobs/new')} className="mt-4 px-4 py-2 rounded-full bg-yellow-400 text-black text-xs font-bold" data-testid="jobs-empty-create">
                Post your first request →
              </button>
            )}
          </div>
        ) : tab === 'offers' ? (
          <div className="space-y-3">
            {offers.map(({ offer, request }) => (
              <Link
                key={offer.offer_id}
                to={request ? `/jobs/${request.request_id}` : '#'}
                className="block rounded-2xl bg-[#13153A]/80 border border-white/5 hover:border-fuchsia-500/30 p-4 transition-all"
                data-testid={`jobs-offer-${offer.offer_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{request?.title || 'Request'}</p>
                    <p className="text-zinc-400 text-xs truncate mt-0.5">Your offer: {offer.message}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full border text-[10px] font-bold uppercase ${STATUS_PILL[offer.status] || STATUS_PILL.open}`}>
                    {offer.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-zinc-400 text-xs">
                  {offer.price && <span className="flex items-center gap-1"><DollarSign size={11} />{offer.price} {offer.currency}</span>}
                  {offer.turnaround_days && <span className="flex items-center gap-1"><Clock size={11} />{offer.turnaround_days}d</span>}
                  <span className="ml-auto text-fuchsia-300">{fmtAgo(offer.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((r) => <RfqCard key={r.request_id} r={r} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function RfqCard({ r }) {
  return (
    <Link
      to={`/jobs/${r.request_id}`}
      className="block rounded-2xl bg-[#13153A]/80 border border-white/5 hover:border-fuchsia-500/30 p-5 transition-all hover:shadow-[0_12px_40px_-15px_rgba(217,70,239,0.5)]"
      data-testid={`rfq-card-${r.request_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-white font-semibold text-base flex-1">{r.title}</h3>
        <span className={`px-2 py-1 rounded-full border text-[10px] font-bold uppercase ${STATUS_PILL[r.status] || STATUS_PILL.open}`}>
          {r.status}
        </span>
      </div>
      <p className="text-zinc-300 text-sm line-clamp-2 mb-3">{r.description}</p>
      <div className="flex items-center gap-4 text-xs text-zinc-400 flex-wrap">
        {r.category_name && (
          <span className="flex items-center gap-1.5"><Tag size={11} className="text-fuchsia-300" />{r.category_name}</span>
        )}
        {(r.budget_min || r.budget_max) && (
          <span className="flex items-center gap-1.5">
            <DollarSign size={11} className="text-yellow-400" />
            {r.budget_min || '?'}–{r.budget_max || '?'} {r.currency}
          </span>
        )}
        {r.turnaround_days && (
          <span className="flex items-center gap-1.5"><Clock size={11} className="text-cyan-300" />{r.turnaround_days}d turnaround</span>
        )}
        <span className="flex items-center gap-1.5 ml-auto"><Eye size={11} />{r.view_count || 0}</span>
        <span>{r.offers_count || 0} offers</span>
        <ArrowRight size={11} />
      </div>
    </Link>
  );
}
